import React, { createContext, useState, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Read synchronously during the first render, not in an effect: route guards
// check `user` while rendering, so restoring it afterwards would bounce an
// already-signed-in admin to the login page on every refresh.
const storedUser = () => {
    try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null; // corrupt entry must not take down the whole app
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(storedUser);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

    const register = useCallback(async (email, password, firstName, lastName) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(`${apiBaseUrl}/users/register`, {
                email,
                password,
                firstName,
                lastName
            });
            setToken(response.data.token);
            setUser(response.data.user);
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            return response.data;
        } catch (err) {
            const message = err.response?.data?.message || 'Registration failed';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl]);

    const login = useCallback(async (email, password) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(`${apiBaseUrl}/users/login`, {
                email,
                password
            });
            setToken(response.data.token);
            setUser(response.data.user);
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            return response.data;
        } catch (err) {
            const message = err.response?.data?.message || 'Login failed';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl]);

    // Admin login
    const adminLogin = useCallback(async (email, password) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(`${apiBaseUrl}/users/admin-login`, {
                email,
                password
            });
            setToken(response.data.token);
            setUser(response.data.user);
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            localStorage.setItem('isAdmin', 'true');
            return response.data;
        } catch (err) {
            const message = err.response?.data?.message || 'Admin login failed';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl]);

    const logout = useCallback(() => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('isAdmin');
    }, []);

    const value = {
        user,
        token,
        loading,
        error,
        register,
        login,
        adminLogin,
        logout,
        isAuthenticated: !!token,
        isAdmin: user?.role === 'admin'
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}

export default AuthContext;
